/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useMediaQuery } from 'hooks/useMediaQuery';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './DifferenceSection.module.scss';

import type { DifferenceSectionProps } from './DifferenceSection.types';

const ITEM_HEIGHT = 560;
const SCROLL_BUFFER = 0.1;

export const DifferenceSection = ({
  title,
  extra,
  description,
  items,
  align = 'center',
  background,
  reverse = false,
  actions,
  className,
}: DifferenceSectionProps) => {
  const isDesktop = useMediaQuery(768);
  const containerRef = useRef<HTMLElement>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToItem = (index: number) => {
    if (!containerRef.current || !isDesktop) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerTop = containerRect.top + window.scrollY;
    const containerHeight = containerRect.height;
    const viewportHeight = window.innerHeight;

    const progressPerElement = (1 - 2 * SCROLL_BUFFER) / items.length;
    const targetProgress = SCROLL_BUFFER + index * progressPerElement + progressPerElement / 2;
    const targetScrollY = containerTop - viewportHeight + targetProgress * (containerHeight + viewportHeight);

    window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isDesktop) {
      return undefined;
    }

    const handleScroll = () => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const scrollProgress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (rect.height + viewportHeight)));

      let currentIndex: number;

      if (scrollProgress <= SCROLL_BUFFER) {
        currentIndex = 0;
      } else if (scrollProgress >= 1 - SCROLL_BUFFER) {
        currentIndex = items.length - 1;
      } else {
        const adjustedProgress = (scrollProgress - SCROLL_BUFFER) / (1 - 2 * SCROLL_BUFFER);
        currentIndex = Math.min(items.length - 1, Math.floor(adjustedProgress * items.length));
      }

      setActiveIndex(currentIndex);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDesktop, items.length]);

  const activeItem = items[activeIndex];
  const progress = items.length > 1 ? (activeIndex / (items.length - 1)) * 100 : 100;

  return (
    <section
      ref={containerRef}
      className={cn(styles.section, className)}
      style={background ? { background } : undefined}
    >
      <div className={styles.container}>
        <div className={styles.scrollArea} style={isDesktop ? { height: items.length * ITEM_HEIGHT } : undefined}>
          <div className={styles.stickyWrap}>
            <TitleBlock
              title={title}
              extra={extra}
              description={description}
              align={align}
              className={cn(styles.header, align === 'left' && styles.headerLeft)}
            />

            <div className={cn(styles.content, reverse && styles.contentReverse)}>
              {isDesktop && (
                <div className={styles.mediaCol}>
                  <div className={styles.mediaGlow} />
                  <div key={activeIndex} className={styles.mediaCard}>
                    {activeItem.media}
                  </div>
                </div>
              )}

              <div className={styles.rail}>
                <div className={styles.railTrack}>
                  <div className={styles.railProgress} style={{ height: `${progress}%` }} />
                </div>

                <div className={styles.tabs}>
                  {items.map((item, index) => {
                    const isActive = !isDesktop || activeIndex === index;

                    return (
                      <div key={item.number} className={cn(styles.tab, isActive && styles.tabActive)}>
                        {!isDesktop && <div className={styles.mobileMedia}>{item.media}</div>}

                        <button className={styles.tabHeader} onClick={() => scrollToItem(index)} disabled={!isDesktop}>
                          <span className={styles.tabIndex}>{item.number}</span>

                          <span className={styles.tabBody}>
                            <span className={styles.tabTitle}>{item.title}</span>
                          </span>
                        </button>

                        {isActive && (
                          <div className={styles.panel}>
                            <div className={styles.panelDescription}>{item.description}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {actions && <div className={styles.actions}>{actions}</div>}
          </div>
        </div>
      </div>
    </section>
  );
};
